import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import CfLoader from "../../common/CfLoader/CfLoader";
import VerificationNavbar from "../CombinedValidate/VerificationNavbar";
import ProgressStepper from "../CombinedValidate/ProgressStepper";
import { Trash2, ChevronDown, ChevronUp, Edit2 } from "react-feather";
import InformationIcon from "../../resources/icons/Information-blue-icon.svg";
import { TwoPagePdfViewer } from "../../components/TwoPagePdfViewer/TwoPagePdfViewer";
import { formStructureBuilder } from "../../utils/formStructureBuilder";
import { formatLabel } from "../../utils/formatLabel";
import Select from "react-select";
import {
  convertDateFromInputFormat,
  convertDateToInputFormat,
} from "../../utils/dateConversion";
import { useTranslation } from "react-i18next";
import { detectFileType } from "../../utils/DetectFileType";
import { useBrowserWarningNavigate } from "../../hooks/useBrowserWarningNavigate";

const MANDATORY_FIELD_MAX_LENGTH = 100;
const REQUIRED_FIELD_VALIDATORS = {
  recipient_name: {
    isValid: (value) => !value || /^[\p{L}\s.'-]+$/u.test(value),
    errorKey: "recipient_name_invalid_characters",
  },
  roll_number: {
    isValid: (value) => !value || /^[a-zA-Z0-9\s./_-]+$/.test(value),
    errorKey: "roll_number_invalid_characters",
  },
};

const OcrForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { t } = useTranslation();

  useBrowserWarningNavigate(t("verify:unsaved_changes_warning"));

  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState("vc");
  const [currentStep, setCurrentStep] = useState(1);
  const [formStructure, setFormStructure] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [s3FileUrl, setS3FileUrl] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [deletedFields, setDeletedFields] = useState(new Set());
  const [deletedArrayItemFields, setDeletedArrayItemFields] = useState(
    new Set(),
  );
  const [isLoadingBackData, setIsLoadingBackData] = useState(false);
  const [issuerList, setIssuerList] = useState([]);
  const [selectedIssuer, setSelectedIssuer] = useState(null);
  const [isLoadingIssuers, setIsLoadingIssuers] = useState(false);
  const [programmeList, setProgrammeList] = useState([]);
  const [selectedProgramme, setSelectedProgramme] = useState(null);
  const [isLoadingProgrammes, setIsLoadingProgrammes] = useState(false);
  const [ocrProgrammeName, setOcrProgrammeName] = useState(null);
  const [ocrOrgName, setOcrOrgName] = useState(null);
  const [ocrOrgCode, setOcrOrgCode] = useState(null);
  const [ocrProgrammeCode, setOcrProgrammeCode] = useState(null);

  const [programmeName, setProgrammeName] = useState("");
  const [pendingProgrammeCode, setPendingProgrammeCode] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [rollNumber, setRollNumber] = useState("");
  const [errors, setErrors] = useState({});

  const steps = [
    t("verify:ocr"),
    t("verify:details"),
    t("verify:payment"),
    t("verify:summary"),
  ];
  const MANDATORY_KEYS = ["programme_name", "recipient_name", "roll_number"];

  const hasValidValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return false;
    }
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }
    if (typeof value === "object" && !Array.isArray(value)) {
      return Object.keys(value).length > 0;
    }
    return true;
  };

  const toKebab = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const getCharacterCount = (value) => String(value || "").length;

  const isOverCharacterLimit = (value) =>
    getCharacterCount(value) > MANDATORY_FIELD_MAX_LENGTH;

  const getRequiredFieldValidationError = (name, value) => {
    if (isOverCharacterLimit(value)) {
      return t("verify:field_character_limit_error", {
        max: MANDATORY_FIELD_MAX_LENGTH,
      });
    }

    const validator = REQUIRED_FIELD_VALIDATORS[name];
    if (validator && !validator.isValid(value)) {
      return t(`verify:${validator.errorKey}`);
    }

    return null;
  };

  const renderRequiredFieldFeedback = (name, value) => {
    const characterCount = getCharacterCount(value);
    const validationError = getRequiredFieldValidationError(name, value);

    return (
      <div className="mt-1 flex items-center justify-between gap-2">
        {validationError ? (
          <p
            id={`ocr-required-${toKebab(name)}-error`}
            className="text-xs text-red-500"
          >
            {validationError}
          </p>
        ) : (
          <span aria-hidden="true" />
        )}
        <p
          className={`ml-auto text-xs ${
            validationError ? "text-red-500" : "text-gray-500"
          }`}
        >
          {characterCount}/{MANDATORY_FIELD_MAX_LENGTH}
        </p>
      </div>
    );
  };

  const hasMandatoryFieldOverLimit = () =>
    [recipientName, rollNumber].some(isOverCharacterLimit);

  const getMandatoryFieldValidationError = () =>
    getRequiredFieldValidationError("recipient_name", recipientName) ||
    getRequiredFieldValidationError("roll_number", rollNumber);

  const fetchAllowedIssuers = async () => {
    try {
      setIsLoadingIssuers(true);
      const response = await GET(EntityConstants.GET_ISSUER);

      if (response.ok) {
        const result = await response.json();
        if (result.data && Array.isArray(result.data)) {
          setIssuerList(result.data);
        } else {
          toast.error(t("verify:invalid_issuer_data_format"));
          console.log("Invalid issuer data format");
        }
      } else {
        toast.error(t("verify:failed_fetch_issuers"));
        console.log("Failed to fetch issuers");
      }
    } catch (error) {
      console.error("Error fetching allowed issuers:", error);
      toast.error(t("verify:error_loading_issuers"));
    } finally {
      setIsLoadingIssuers(false);
    }
  };

  const handleIssuerChange = (selectedOption) => {
    setSelectedIssuer(selectedOption);
    setSelectedProgramme(null);
    setProgrammeName("");
    setProgrammeList([]);
    setPendingProgrammeCode("");
    getProgrammeDetails(selectedOption.code);
    setErrors((prevErrors) => ({
      ...prevErrors,
      issuer: undefined,
    }));
  };

  const getProgrammeDetails = async (orgCode) => {
    try {
      setIsLoadingProgrammes(true);
      const response = await GET(
        `${EntityConstants.GET_PROGRAMME}?code=${orgCode}`,
      );
      if (response.ok) {
        const data = await response.json();
        setProgrammeList(data.data);
      } else {
        toast.error(t("verify:failed_fetch_programmes"));
      }
    } catch (error) {
      console.error("Error fetching programme details:", error);
      toast.error(t("verify:error_loading_programmes"));
    } finally {
      setIsLoadingProgrammes(false);
    }
  };

  const handleProgrammeChange = (selectedOption) => {
    setSelectedProgramme(selectedOption);
    setProgrammeName(selectedOption.value);
    setErrors((prevErrors) => ({
      ...prevErrors,
      programme: undefined,
    }));
  };

  const fetchOcrDataFromApi = async (txnId) => {
    try {
      setIsLoadingBackData(true);

      const response = await GET(
        `${EntityConstants.GET_UPDATED_FORM_DATA}/${txnId}`,
      );

      if (response.ok) {
        const apiResponse = await response.json();

        if (apiResponse.data) {
          const rawOcrData = apiResponse.data.verification_data;
          const orgCode = apiResponse.data.org_code;
          const prog = rawOcrData["programme_name"] || "";
          const recipient = rawOcrData["recipient_name"] || "";
          const roll = rawOcrData["roll_number"] || "";
          setProgrammeName(prog);
          setRecipientName(recipient);
          setRollNumber(roll);
          setPendingProgrammeCode(apiResponse.data.programme_code || "");

          const cleanedData = {};
          Object.entries(rawOcrData).forEach(([key, value]) => {
            if (hasValidValue(value)) cleanedData[key] = value;
          });

          setFormStructure(formStructureBuilder(cleanedData));
          setFormData(cleanedData);

          setDeletedFields(new Set());
          setDeletedArrayItemFields(new Set());

          const initialExpanded = {};
          Object.keys(cleanedData).forEach((key) => {
            if (Array.isArray(cleanedData[key])) initialExpanded[key] = true;
          });
          setExpandedSections(initialExpanded);

          if (apiResponse.s3_file_url) setS3FileUrl(apiResponse.s3_file_url);
          if (apiResponse.data.file_url) setFileUrl(apiResponse.data.file_url);

          if (orgCode && issuerList.length > 0) {
            const matchedIssuer = issuerList.find(
              (issuer) => issuer.org_code === orgCode,
            );
            if (matchedIssuer) {
              setSelectedIssuer({
                value: matchedIssuer.org_name,
                label: matchedIssuer.org_name,
                code: matchedIssuer.org_code,
              });
              getProgrammeDetails(matchedIssuer.org_code);
            }
          }

          toast.success(t("verify:form_data_loaded_success"));
        } else {
          toast.error(t("verify:invalid_api_response"));
        }
      } else {
        toast.error(t("verify:failed_fetch_form_data"));
      }
    } catch (error) {
      console.error("Error fetching OCR data from API:", error);
      toast.error(t("verify:error_loading_form_data"));
    } finally {
      setIsLoadingBackData(false);
    }
  };

  useEffect(() => {
    if (pendingProgrammeCode && programmeList.length > 0) {
      const found = programmeList.find(
        (p) => p.programme_code === pendingProgrammeCode,
      );
      if (found) {
        setSelectedProgramme({
          value: found.programme_name,
          label: found.programme_display_name
            ? `${found.programme_display_name} (${found.programme_name})`
            : found.programme_name,
          code: found.programme_code,
        });
        setProgrammeName(found.programme_name);
        setPendingProgrammeCode("");
      }
    }
  }, [pendingProgrammeCode, programmeList]);

  useEffect(() => {
    fetchAllowedIssuers();
  }, []);

  const isValidOcrValue = (val) =>
    val && typeof val === "string" && val.trim() !== "" && val !== "Unknown";

  useEffect(() => {
    const isComingFromPayment = state?.fromPayment === true;
    const txnId = state?.transactionId;

    if (isComingFromPayment && txnId) {
      fetchOcrDataFromApi(txnId);
    } else if (state?.ocrData) {
      const structure = formStructureBuilder(state.ocrData);
      setFormStructure(structure);
      setFormData(state.ocrData);

      setProgrammeName(state.ocrData["programme_name"] || "");
      setRecipientName(state.ocrData["recipient_name"] || "");
      setRollNumber(state.ocrData["roll_number"] || "");

      const initialExpanded = {};
      Object.keys(structure).forEach((key) => {
        if (structure[key].isArray) {
          initialExpanded[key] = true;
        }
      });
      setExpandedSections(initialExpanded);

      if (
        state?.programmeDetails &&
        isValidOcrValue(state.programmeDetails.programme_name)
      ) {
        setOcrProgrammeName(state.programmeDetails.programme_name);
        setOcrProgrammeCode(state.programmeDetails.programme_code || null);
      }
      if (state?.orgDetails && isValidOcrValue(state.orgDetails.org_name)) {
        setOcrOrgName(state.orgDetails.org_name);
        setOcrOrgCode(state.orgDetails.org_code || null);
      }
    }

    if (state?.s3FileUrl) {
      setS3FileUrl(state.s3FileUrl);
    }
    if (state?.originalFileUrl) {
      setFileUrl(state.originalFileUrl);
    }
  }, [
    state?.ocrData,
    state?.s3FileUrl,
    state?.fromPayment,
    state?.transactionId,
    issuerList,
  ]);

  const renderRequiredField = (label, value, setter, name) => {
    const validationError = getRequiredFieldValidationError(name, value);
    const fieldId = `ocr-required-${toKebab(name)}`;

    return (
      <div className="flex-1">
        <input
          id={fieldId}
          type="text"
          value={value}
          onChange={(e) => setter(e.target.value)}
          maxLength={MANDATORY_FIELD_MAX_LENGTH}
          aria-invalid={Boolean(validationError)}
          aria-describedby={validationError ? `${fieldId}-error` : undefined}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
            validationError
              ? "border-red-500 focus:ring-red-200"
              : "border-gray-300 focus:ring-blue-500"
          }`}
          name={name}
          required
        />
        {renderRequiredFieldFeedback(name, value)}
      </div>
    );
  };

  const isDateField = (key) => {
    const lowerKey = key.toLowerCase();

    const dateKeywords = [
      "date",
      "start",
      "end",
      "born",
      "dob",
      "expiry",
      "expire",
      "expiration",
      "valid",
      "issued",
      "created",
      "updated",
    ];

    return dateKeywords.some((word) => lowerKey.includes(word));
  };

  const handleFieldChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDeleteSimpleField = (key) => {
    if (
      window.confirm(
        `${t("verify:confirm_delete_the")} "${formatLabel(key)}" ${t(
          "verify:field",
        )}?`,
      )
    ) {
      setDeletedFields((prev) => new Set([...prev, key]));

      setFormData((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });

      toast.success(`"${formatLabel(key)}" ${t("verify:field_deleted")}`);
    }
  };

  const handleArrayItemChange = (arrayKey, index, fieldKey, value) => {
    setFormData((prev) => {
      const updatedArray = [...prev[arrayKey]];
      updatedArray[index] = {
        ...updatedArray[index],
        [fieldKey]: value,
      };
      return {
        ...prev,
        [arrayKey]: updatedArray,
      };
    });
  };

  const removeArrayItem = (arrayKey, index) => {
    if (window.confirm(`${t("verify:confirm_delete_item")}`)) {
      setDeletedArrayItemFields((prev) => {
        const newSet = new Set(prev);
        const prefix = `${arrayKey}_${index}_`;
        newSet.forEach((key) => {
          if (key.startsWith(prefix)) {
            newSet.delete(key);
          }
        });
        return newSet;
      });

      setFormData((prev) => {
        const updatedArray = prev[arrayKey].filter((_, i) => i !== index);

        if (updatedArray.length === 0) {
          const updated = { ...prev };
          delete updated[arrayKey];
          return updated;
        }

        return {
          ...prev,
          [arrayKey]: updatedArray,
        };
      });
      toast.success(t("verify:item_deleted_success"));
    }
  };

  const handleDeleteArrayItemField = (arrayKey, index, fieldKey) => {
    if (
      window.confirm(
        `${t("verify:confirm_delete_the")} "${formatLabel(fieldKey)}" ${t(
          "verify:field",
        )}?`,
      )
    ) {
      const compositeKey = `${arrayKey}_${index}_${fieldKey}`;
      setDeletedArrayItemFields((prev) => new Set([...prev, compositeKey]));
      setFormData((prev) => {
        const updatedArray = [...prev[arrayKey]];
        const updatedItem = { ...updatedArray[index] };
        delete updatedItem[fieldKey];
        updatedArray[index] = updatedItem;
        return {
          ...prev,
          [arrayKey]: updatedArray,
        };
      });

      toast.success(`"${formatLabel(fieldKey)}" ${t("verify:field_deleted")}`);
    }
  };

  const handleDeleteArraySection = (arrayKey) => {
    if (
      window.confirm(
        `${t("verify:confirm_delete_entire")} "${formatLabel(arrayKey)}" ${t(
          "verify:section",
        )}?`,
      )
    ) {
      setDeletedArrayItemFields((prev) => {
        const newSet = new Set(prev);
        const prefix = `${arrayKey}_`;
        newSet.forEach((key) => {
          if (key.startsWith(prefix)) {
            newSet.delete(key);
          }
        });
        return newSet;
      });

      setFormData((prev) => {
        const updated = { ...prev };
        delete updated[arrayKey];
        return updated;
      });

      toast.success(t("verify:section_deleted_success"));
    }
  };

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderField = (key, fieldConfig) => {
    const label = formatLabel(key);
    const value = formData[key];

    if (deletedFields.has(key)) {
      return null;
    }

    if (!(key in formData)) {
      return null;
    }

    if (!hasValidValue(value)) {
      return null;
    }

    if (fieldConfig.isArray) {
      if (fieldConfig.type === "arrayOfObjects") {
        return renderArrayOfObjects(key, fieldConfig, label);
      } else {
        return renderSimpleArray(key, fieldConfig, label);
      }
    }

    if (fieldConfig.type === "object") {
      return renderNestedObject(key, fieldConfig, label);
    }

    return renderSimpleField(key, label, value);
  };

  const renderSimpleField = (key, label, value) => {
    if (deletedFields.has(key)) {
      return null;
    }

    if (!(key in formData) || !hasValidValue(value)) {
      return null;
    }

    const isDate = isDateField(key);
    const inputValue = isDate
      ? convertDateToInputFormat(String(value || ""))
      : String(value || "");

    return (
      <div key={key} className="flex-1 min-w-[300px]">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-md hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-semibold text-gray-700">
              {label}
            </label>
            <button
              id={`ocr-delete-field-${toKebab(key)}`}
              type="button"
              onClick={() => handleDeleteSimpleField(key)}
              className="text-red-500 focus:outline-none hover:text-red-700 hover:bg-red-50 p-2 rounded transition-colors"
              title={t("verify:delete_this_field")}
            >
              <Trash2 size={16} />
            </button>
          </div>

          {isDate ? (
            <input
              id={`ocr-date-field-${toKebab(key)}`}
              type="date"
              value={inputValue}
              onChange={(e) =>
                handleFieldChange(
                  key,
                  convertDateFromInputFormat(e.target.value),
                )
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder={`${t("verify:enter")} ${label.toLowerCase()}`}
            />
          ) : (
            <textarea
              id={`ocr-textarea-field-${toKebab(key)}`}
              value={inputValue}
              onChange={(e) => handleFieldChange(key, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm overflow-x-auto whitespace-nowrap"
              rows="1"
              placeholder={`${t("verify:enter")} ${label.toLowerCase()}`}
            />
          )}
        </div>
      </div>
    );
  };

  const renderArrayOfObjects = (key, fieldConfig, label) => {
    const items = formData[key] || [];
    const isExpanded = expandedSections[key];

    if (deletedFields.has(key) || !items || items.length === 0) {
      return null;
    }

    return (
      <div key={key} className="mb-8">
        <div className="bg-white rounded-lg border-none border-gray-200 overflow-hidden shadow-xl/30">
          <button
            id={`ocr-toggle-array-section-${toKebab(key)}`}
            type="button"
            onClick={() => toggleSection(key)}
            className="w-full px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 flex items-center justify-between border-y border-gray-200 focus:outline-none transition-colors"
          >
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronUp size={20} className="text-cfPrimary" />
              ) : (
                <ChevronDown size={20} className="text-cfPrimary" />
              )}
              <span className="font-semibold text-gray-800">{label}</span>
              <span className="ml-2 px-3 py-1 bg-blue-200 text-cfPrimary rounded-full text-xs font-medium">
                {items.length} item{items.length !== 1 ? "s" : ""}
              </span>
            </div>
            <button
              id={`ocr-delete-array-section-${toKebab(key)}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteArraySection(key);
              }}
              className="text-red-600 focus:outline-none hover:text-red-800 hover:bg-red-50 p-2 rounded transition-colors"
              title={t("verify:delete_entire_section")}
            >
              <Trash2 size={18} />
            </button>
          </button>

          {isExpanded && (
            <div className="bg-white space-y-6">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="border-none rounded-lg p-6 border-t bg-gradient-to-br from-gray-50 to-white hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-cfPrimary text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      <span className="font-semibold text-gray-800">
                        {label.slice(0, -1)} {t("verify:details")}
                      </span>
                    </div>
                    <button
                      id={`ocr-delete-array-item-${toKebab(key)}-${index + 1}`}
                      type="button"
                      onClick={() => removeArrayItem(key, index)}
                      className="text-red-600 focus:outline-none hover:text-red-800 hover:bg-red-50 p-2 rounded transition-colors"
                      title={t("verify:delete_item")}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-4">
                    {Object.entries(item).map(([fieldKey, fieldValue]) => {
                      const compositeKey = `${key}_${index}_${fieldKey}`;

                      if (deletedArrayItemFields.has(compositeKey)) {
                        return null;
                      }

                      if (!hasValidValue(fieldValue)) {
                        return null;
                      }

                      const fieldLabel = formatLabel(fieldKey);
                      const isDate = isDateField(fieldKey);
                      const inputValue = isDate
                        ? convertDateToInputFormat(String(fieldValue || ""))
                        : String(fieldValue || "");

                      return (
                        <div key={fieldKey} className="flex-1 min-w-[280px]">
                          <div className="bg-white rounded-lg border-none border-gray-200 p-4 transition-colors">
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                                {fieldLabel}
                              </label>
                              <button
                                id={`ocr-delete-array-item-field-${toKebab(key)}-${index + 1}-${toKebab(fieldKey)}`}
                                type="button"
                                onClick={() =>
                                  handleDeleteArrayItemField(
                                    key,
                                    index,
                                    fieldKey,
                                  )
                                }
                                className="text-red-500 focus:outline-none hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                                title={t("verify:delete_this_field")}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>

                            {isDate ? (
                              <input
                                id={`ocr-date-array-item-field-${toKebab(key)}-${index + 1}-${toKebab(fieldKey)}`}
                                type="date"
                                value={inputValue}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    key,
                                    index,
                                    fieldKey,
                                    convertDateFromInputFormat(e.target.value),
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                            ) : (
                              <input
                                id={`ocr-text-array-item-field-${toKebab(key)}-${index + 1}-${toKebab(fieldKey)}`}
                                type="text"
                                value={inputValue}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    key,
                                    index,
                                    fieldKey,
                                    e.target.value,
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder={`${t(
                                  "verify:enter",
                                )} ${fieldLabel.toLowerCase()}`}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSimpleArray = (key, fieldConfig, label) => {
    const items = formData[key] || [];
    const isExpanded = expandedSections[key];

    if (deletedFields.has(key) || !items || items.length === 0) {
      return null;
    }

    return (
      <div key={key} className="mb-8">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <button
            id={`ocr-toggle-simple-array-section-${toKebab(key)}`}
            type="button"
            onClick={() => toggleSection(key)}
            className="w-full px-6 py-4 bg-gradient-to-r focus:outline-none from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 flex items-center justify-between border-b border-gray-200 transition-colors"
          >
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronUp size={20} className="text-cfPrimary" />
              ) : (
                <ChevronDown size={20} className="text-cfPrimary" />
              )}
              <span className="font-semibold text-gray-800">{label}</span>
            </div>
            <button
              id={`ocr-delete-simple-array-section-${toKebab(key)}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteArraySection(key);
              }}
              className="text-red-600 focus:outline-none hover:text-red-800 hover:bg-red-50 p-2 rounded transition-colors"
              title={t("verify:delete_entire_section")}
            >
              <Trash2 size={18} />
            </button>
          </button>

          {isExpanded && (
            <div className="p-6 space-y-3">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 hover:bg-gray-100 transition-colors"
                >
                  <span className="text-gray-700 text-sm">{String(item)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderNestedObject = (key, fieldConfig, label) => {
    const isExpanded = expandedSections[key];
    const nestedObj = formData[key];

    if (deletedFields.has(key) || !nestedObj) {
      return null;
    }

    return (
      <div key={key} className="mb-8">
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
          <button
            id={`ocr-toggle-object-section-${toKebab(key)}`}
            type="button"
            onClick={() => toggleSection(key)}
            className="w-full px-6 py-4 bg-gradient-to-r focus:outline-none from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 flex items-center justify-between border-b border-gray-200 transition-colors"
          >
            <div className="flex items-center gap-3">
              {isExpanded ? (
                <ChevronUp size={20} className="text-cfPrimary" />
              ) : (
                <ChevronDown size={20} className="text-cfPrimary" />
              )}
              <span className="font-semibold text-gray-800">{label}</span>
            </div>
            <button
              id={`ocr-delete-object-section-${toKebab(key)}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteArraySection(key);
              }}
              className="text-red-600 focus:outline-none hover:text-red-800 hover:bg-red-50 p-2 rounded transition-colors"
              title={t("verify:delete_entire_section")}
            >
              <Trash2 size={18} />
            </button>
          </button>

          {isExpanded && (
            <div className="p-6">
              <div className="flex flex-wrap gap-4">
                {Object.entries(nestedObj)
                  .filter(([nestedKey, nestedValue]) =>
                    hasValidValue(nestedValue),
                  )
                  .map(([nestedKey, nestedValue]) => (
                    <div key={nestedKey} className="flex-1 min-w-[300px]">
                      <div className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                            {formatLabel(nestedKey)}
                          </label>
                          <button
                            id={`ocr-clear-object-field-${toKebab(key)}-${toKebab(nestedKey)}`}
                            type="button"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `${t("verify:delete")} "${formatLabel(
                                    nestedKey,
                                  )}" ${t("verify:field")}?`,
                                )
                              ) {
                                setFormData((prev) => ({
                                  ...prev,
                                  [key]: {
                                    ...prev[key],
                                    [nestedKey]: "",
                                  },
                                }));
                                toast.success(t("verify:field_cleared"));
                              }
                            }}
                            className="text-red-500 focus:outline-none hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors"
                            title={t("verify:delete_this_field")}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <input
                          id={`ocr-object-field-${toKebab(key)}-${toKebab(nestedKey)}`}
                          type="text"
                          value={nestedValue || ""}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              [key]: {
                                ...prev[key],
                                [nestedKey]: e.target.value,
                              },
                            }));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!programmeName.trim() || !recipientName.trim() || !rollNumber.trim()) {
      toast.error(t("verify:required_fields_msg"));
      return;
    }
    if (!ocrOrgName && !selectedIssuer) {
      toast.error(t("verify:select_issuer_org_msg"));
      return;
    }
    if (!ocrProgrammeName && !selectedProgramme) {
      toast.error(t("verify:select_programme_msg"));
      return;
    }

    const finalFormData = {
      ...formData,
      programme_name: programmeName,
      recipient_name: recipientName,
      roll_number: rollNumber,
    };

    if (hasMandatoryFieldOverLimit()) {
      toast.error(
        t("verify:field_character_limit_submit_error", {
          max: MANDATORY_FIELD_MAX_LENGTH,
        }),
      );
      return;
    }

    const mandatoryFieldValidationError = getMandatoryFieldValidationError();
    if (mandatoryFieldValidationError) {
      toast.error(mandatoryFieldValidationError);
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        org_code: ocrOrgName ? ocrOrgCode || "" : selectedIssuer.code,
        programme_code: ocrProgrammeName
          ? ocrProgrammeCode || ""
          : selectedProgramme.code,
        data: finalFormData,
        file_url: fileUrl,
      };

      const response = await POST(
        `${EntityConstants.UPDATE_POST_FORM_DATA}`,
        payload,
      );

      if (response.ok) {
        const data = await response.json();
        const finalData = data.data;

        const applicationStatus =
          finalData.application_status || finalData.status;

        if (finalData.is_payment_required === true) {
          navigate(
            `/verifier/ocr/payment-details/${finalData.verification_log_id}/${finalData.order_id}`,
            {
              state: {
                issuerName: finalData.issuer_name,
                programmeName: finalData.programme_name,
                razorpayClientId: finalData.razorpay_client_id,
                verificationTotalAmount: finalData.total_verification_cost,
                verificationAmount:
                  finalData.verification_cost?.verification_fee,
                platformFee: finalData.verification_cost?.platform_fee,
                igstPercentage: finalData.verification_cost?.igst_percentage,
                cgstPercentage: finalData.verification_cost?.cgst_percentage,
                sgstPercentage: finalData.verification_cost?.sgst_percentage,
                gstPercentage: finalData.verification_cost?.gst_percentage,
                igst: finalData.verification_cost?.igst_fee,
                cgst: finalData.verification_cost?.cgst_fee,
                sgst: finalData.verification_cost?.sgst_fee,
                gst: finalData.verification_cost?.gst_fee,
                uploadedDocument: finalData.uploaded_document,
                orderId: finalData.order_id,
                verificationLogId: finalData.verification_log_id,
                issuerOrganisation: finalData.issuer_organisation,
                paymentRequired: finalData.is_payment_required,
                application_status: applicationStatus,
                application_status_message: finalData.application_status_message,
                vc_status: finalData.vc_status,
                currentStep: 2,
                flowType: "ocr",
                ocrData: finalFormData,
                transactionId: finalData.verification_log_id,
              },
            },
          );
        } else if (applicationStatus === "Approval Pending") {
          navigate("/verifier/ocr/summary", {
            state: {
              verificationLogId: finalData.verification_log_id,
              orderId: finalData.order_id,
              issuerName: finalData.issuer_name,
              programmeName: finalData.programme_name,
              uploadedDocument: finalData.uploaded_document,
              verificationTotalAmount: finalData.total_verification_cost,
              verificationAmount: finalData.verification_cost?.verification_fee,
              platformFee: finalData.verification_cost?.platform_fee,
              gst: finalData.verification_cost?.gst_fee,
              igst: finalData.verification_cost?.igst_fee,
              sgst: finalData.verification_cost?.sgst_fee,
              cgst: finalData.verification_cost?.cgst_fee,
              gstPercentage: finalData.verification_cost?.gst_percentage,
              igstPercentage: finalData.verification_cost?.igst_percentage,
              sgstPercentage: finalData.verification_cost?.sgst_percentage,
              cgstPercentage: finalData.verification_cost?.cgst_percentage,
              issuerOrganisation: finalData.issuer_organisation,
              status: applicationStatus,
              application_status: applicationStatus,
              application_status_message: finalData.application_status_message,
              vc_status: finalData.vc_status,
              paymentStatus: finalData.payment_status,
              documentType: finalData.document_type,
              modeOfVerification: finalData.mode_of_verification,
              comments: finalData.comments,
              ocrData: finalFormData,
              flowType: "ocr",
            },
          });
        } else {
          navigate("/verifier/ocr/summary", {
            state: {
              verificationLogId: finalData.verification_log_id,
              orderId: finalData.order_id,
              issuerName: finalData.issuer_name,
              programmeName: finalData.programme_name,
              uploadedDocument: finalData.uploaded_document,
              verificationTotalAmount: finalData.total_verification_cost,
              verificationAmount: finalData.verification_cost?.verification_fee,
              platformFee: finalData.verification_cost?.platform_fee,
              gst: finalData.verification_cost?.gst_fee,
              igst: finalData.verification_cost?.igst_fee,
              sgst: finalData.verification_cost?.sgst_fee,
              cgst: finalData.verification_cost?.cgst_fee,
              gstPercentage: finalData.verification_cost?.gst_percentage,
              igstPercentage: finalData.verification_cost?.igst_percentage,
              sgstPercentage: finalData.verification_cost?.sgst_percentage,
              cgstPercentage: finalData.verification_cost?.cgst_percentage,
              issuerOrganisation: finalData.issuer_organisation,
              status: applicationStatus,
              application_status: applicationStatus,
              application_status_message: finalData.application_status_message,
              vc_status: finalData.vc_status,
              paymentStatus: finalData.payment_status,
              documentType: finalData.document_type,
              modeOfVerification: finalData.mode_of_verification,
              comments: finalData.comments,
              ocrData: finalFormData,
              flowType: "ocr",
            },
          });
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || t("verify:failed_update_form_data"));
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(t("verify:error_updating_form_data"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm(`${t("verify:confirm_cancel_unsaved")}`)) {
      navigate("/verifier/validate");
    }
  };

  const handleSectionChange = (section) => {
    if (section === "vc") {
      navigate("/verifier/validate");
    }
  };

  const simpleFields = Object.entries(formStructure).filter(
    ([key, config]) =>
      !MANDATORY_KEYS.includes(key) &&
      !config.isArray &&
      config.type === "text" &&
      !deletedFields.has(key) &&
      key in formData &&
      hasValidValue(formData[key]),
  );

  const complexFields = Object.entries(formStructure).filter(
    ([key, config]) =>
      !MANDATORY_KEYS.includes(key) &&
      (config.isArray || config.type === "object") &&
      !deletedFields.has(key) &&
      key in formData &&
      hasValidValue(formData[key]) &&
      (Array.isArray(formData[key])
        ? formData[key].length > 0
        : Object.keys(formData[key]).length > 0),
  );

  if (isLoadingBackData) {
    return (
      <div className="min-h-screen py-[6rem] bg-white px-4 flex items-center justify-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-[6rem] bg-white px-4">
      <VerificationNavbar
        activeSection={activeSection}
        handleSectionChange={handleSectionChange}
      />
      <ProgressStepper steps={steps} currentStep={currentStep} />

      <div className="w-full mx-auto">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200/60 overflow-hidden backdrop-blur-sm py-6 px-4">
          <div className="bg-[#EAEBFF] border-l-4 border-cfPrimary rounded-lg p-6 mb-8">
            <div className="flex gap-3">
              <img src={InformationIcon} height={20} width={20} alt="info" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">
                  {t("verify:important_information")}
                </h4>
                <p className="text-sm text-gray-800">
                  {t("verify:extracted_data_edit_info")}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row gap-8 mb-8">
            {s3FileUrl && (
              <div className="w-full lg:w-[40%]">
                <div className="sticky top-6 h-full rounded-xl overflow-hidden">
                  {detectFileType(s3FileUrl) === "pdf" ? (
                    <TwoPagePdfViewer
                      pdfUrl={s3FileUrl}
                      viewerClassName="bg-gray-200 border border-gray-200"
                    />
                  ) : (
                    <div className="w-full border border-gray-300 rounded-lg overflow-hidden">
                      <div className="w-full h-[400px] bg-gray-100 flex items-center justify-center">
                        <img src={s3FileUrl} alt="Document" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div
              className={`${
                s3FileUrl ? "w-full lg:w-[60%]" : "w-full"
              } h-[44rem] overflow-y-auto`}
            >
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col xl:flex-row w-full gap-4">
                  <div className="w-full xl:w-[50%] bg-white rounded-lg border border-gray-200 p-6 shadow-md mb-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {t("verify:select_issuer_organisation")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    {ocrOrgName ? (
                      <input
                        id="ocr-selected-issuer-organisation"
                        type="text"
                        value={ocrOrgName}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg !bg-gray-100 text-gray-700 cursor-not-allowed focus:outline-none"
                      />
                    ) : (
                      <>
                        <Select
                          id="issuerName"
                          inputId="ocr-issuer-select"
                          name="issuerName"
                          value={selectedIssuer}
                          onChange={handleIssuerChange}
                          options={issuerList.map((issuer) => ({
                            value: issuer.org_name,
                            label: issuer.org_name,
                            code: issuer.org_code,
                          }))}
                          placeholder={t("verify:select_issuer")}
                          className="basic-single"
                          classNamePrefix="select"
                          isDisabled={
                            issuerList.length === 0 || isLoadingIssuers
                          }
                          isLoading={isLoadingIssuers}
                        />
                        {!selectedIssuer && (
                          <p className="text-xs text-red-500 mt-2">
                            {t("verify:select_issuer_proceed_msg")}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  <div className="w-full xl:w-[50%] bg-white rounded-lg border border-gray-200 p-6 shadow-md mb-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {t("verify:program_name")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    {ocrProgrammeName ? (
                      <input
                        id="ocr-selected-programme-name"
                        type="text"
                        value={ocrProgrammeName}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg !bg-gray-100 text-gray-700 cursor-not-allowed focus:outline-none"
                      />
                    ) : (
                      <>
                        <Select
                          id="programmeName"
                          inputId="ocr-programme-select"
                          name="programmeName"
                          value={selectedProgramme}
                          onChange={handleProgrammeChange}
                          options={programmeList.map((programme) => ({
                            value: programme.programme_name,
                            label: programme.programme_display_name
                              ? `${programme.programme_display_name} (${programme.programme_name})`
                              : programme.programme_name,
                            code: programme.programme_code,
                          }))}
                          placeholder={t("verify:select_programme")}
                          className="basic-single"
                          classNamePrefix="select"
                          isDisabled={
                            programmeList.length === 0 || isLoadingProgrammes
                          }
                          isLoading={isLoadingProgrammes}
                        />
                        {errors.programme && (
                          <p className="text-xs text-red-500 mt-2">
                            {errors.programme}
                          </p>
                        )}
                        {!selectedProgramme && (
                          <p className="text-xs text-red-500 mt-2">
                            {t("verify:select_programme_proceed_msg")}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col xl:flex-row w-full gap-4">
                  <div className="w-full xl:w-[50%] bg-white rounded-lg border border-gray-200 p-6 shadow-md mb-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {t("verify:recipient_name")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    {renderRequiredField(
                      "Recipient Name",
                      recipientName,
                      setRecipientName,
                      "recipient_name",
                    )}
                  </div>
                  <div className="w-full xl:w-[50%] bg-white rounded-lg border border-gray-200 p-6 shadow-md mb-3">
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {t("verify:roll_number")}{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    {renderRequiredField(
                      "Roll Number",
                      rollNumber,
                      setRollNumber,
                      "roll_number",
                    )}
                  </div>
                </div>
                <div className="space-y-8 mb-8">
                  {simpleFields.length > 0 && (
                    <div className="flex flex-wrap gap-4">
                      {simpleFields.map(([key, fieldConfig]) =>
                        renderSimpleField(key, formatLabel(key), formData[key]),
                      )}
                    </div>
                  )}

                  {complexFields.length > 0 && (
                    <div className="space-y-8">
                      {complexFields.map(([key, fieldConfig]) =>
                        renderField(key, fieldConfig),
                      )}
                    </div>
                  )}

                  {simpleFields.length === 0 && complexFields.length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">📭</div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">
                        {t("verify:no_fields_available")}
                      </h3>
                      <p className="text-gray-600">
                        {t("verify:all_fields_deleted_msg")}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-4 justify-between sticky bottom-0 bg-white pt-4 border-t border-gray-200">
                  <button
                    id="ocr-cancel-button"
                    type="button"
                    onClick={handleCancel}
                    className="py-2.5 px-[3.40rem] text-sm font-medium text-gray-700 bg-white hover:!bg-gray-50 focus:outline-none rounded-[8px] border border-gray-300 focus:z-10 focus:ring-3 focus:ring-gray-100"
                  >
                    {t("verify:cancel")}
                  </button>
                  <button
                    id="ocr-save-and-continue-button"
                    type="submit"
                    disabled={
                      isSubmitting ||
                      (!ocrOrgName && !selectedIssuer) ||
                      (!ocrProgrammeName && !selectedProgramme)
                    }
                    className={`font-semibold px-4 py-2 rounded-[8px] ${
                      isSubmitting ||
                      (!ocrOrgName && !selectedIssuer) ||
                      (!ocrProgrammeName && !selectedProgramme)
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-cfButton cursor-pointer hover:bg-cfPrimaryDark"
                    } text-white`}
                  >
                    {isSubmitting
                      ? t("verify:saving_processing")
                      : t("verify:save_continue")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OcrForm;
